import { useRateFieldError } from "../../hooks/useRateFieldError";
import { FC, useState } from "react";

import { Box, Grid, useTheme } from "@mui/material";
import { MembershipValidityPeriod } from "@simplyk/common";
import { useFormContext, useWatch } from "react-hook-form";

import { useRateContext } from "../../Context/RateContext";
import { useRateFieldName } from "../../hooks/useRateFieldName";
import { ConfirmRateChangeDialog } from "../ConfirmRateChangeDialog/ConfirmRateChangeDialog";

import { isRequired } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardDatePicker } from "@/dashboard-design-system/DashboardDatePicker";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

export const ValidityAtDatePicker: FC = () => {
  const { t } = useTranslate();
  const theme = useTheme();
  const { hasExistingPayments, canModifyValidityAtDate, unlockValidityAtDate } = useRateContext();
  const { control } = useFormContext<StepTicketsFormData>();

  const validityField = useRateFieldName("membershipValidityPeriod");
  const atDateField = useRateFieldName("membershipValidityPeriodAtDate");

  const currentValidity = useWatch({ control, name: validityField });
  const isVisible = currentValidity === MembershipValidityPeriod.AtDate;
  const error = useRateFieldError("membershipValidityPeriodAtDate");

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  if (!isVisible) {
    return null;
  }

  const handleClick = () => {
    if (hasExistingPayments && !isConfirmOpen && !canModifyValidityAtDate) {
      setIsConfirmOpen(true);
    }
  };

  const handleConfirm = () => {
    unlockValidityAtDate();
    setIsConfirmOpen(false);
  };

  return (
    <>
      <Grid item marginBottom={error?.message ? -3.25 : -0.75} marginTop={1}>
        {/* Disabled MUI inputs swallow clicks — this wrapper catches the unlock click while locked. */}
        <Grid
          container
          onClick={handleClick}
          data-test="validity-at-date-pickers"
          sx={{ cursor: canModifyValidityAtDate ? undefined : "pointer" }}
        >
          <Grid item sx={{ width: "100px", maxWidth: "80px" }}>
            <Box sx={{ pointerEvents: canModifyValidityAtDate ? undefined : "none" }}>
              <DashboardDatePicker
                name={atDateField}
                datePickerViews={["day"]}
                openTo="day"
                control={control}
                errorMessage={error?.message}
                rules={[isRequired(isVisible, t("dashboard", "common.isRequired"))]}
                disabled={!canModifyValidityAtDate}
                placeholder={t("dashboard", "datePicker.day")}
              />
            </Box>
          </Grid>
          <Grid item sx={{ width: "110px", maxWidth: "110px", marginLeft: theme.spacing(1) }}>
            <Box sx={{ pointerEvents: canModifyValidityAtDate ? undefined : "none" }}>
              <DashboardDatePicker
                name={atDateField}
                datePickerViews={["month"]}
                openTo="month"
                control={control}
                errorMessage={error?.message}
                rules={[isRequired(isVisible, t("dashboard", "common.isRequired"))]}
                disabled={!canModifyValidityAtDate}
                placeholder={t("dashboard", "datePicker.month")}
              />
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <ConfirmRateChangeDialog
        open={isConfirmOpen}
        variant="date"
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirm}
      />
    </>
  );
};
